import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import Seo from "@/components/Seo";

export default function CmsLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/cms"); }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/cms");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[#faf7ee]">
      <Seo title="Content Studio Login | Legacy India Travel" />
      {/* Left visual — kept different from /admin/login on purpose */}
      <div className="relative hidden lg:block lg:col-span-3 bg-[#14523d]">
        <img
          src="https://images.pexels.com/photos/31306747/pexels-photo-31306747.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="India"
          className="absolute inset-0 w-full h-full object-cover opacity-70 mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#14523d] via-[#14523d]/50 to-transparent" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-[#e8c970]">
            <ShieldCheck className="w-4 h-4" /> Content Studio
          </div>
          <div>
            <div className="font-serif text-5xl leading-tight">Craft every corner of your website.</div>
            <p className="mt-4 text-white/80 max-w-md">Edit pages, publish stories, curate tours and reply to enquiries — all from one calm workspace made for content teams.</p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-sm">
              {[
                { k: "31+", v: "Tours" },
                { k: "28", v: "Destinations" },
                { k: "6", v: "Editable pages" },
              ].map((m) => (
                <div key={m.v}>
                  <div className="font-serif text-3xl text-[#e8c970]">{m.k}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-white/70">{m.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 lg:col-span-2">
        <form onSubmit={onSubmit} data-testid="cms-login-form" className="w-full max-w-sm">
          <img src={LOGO_URL} alt="Legacy India Travel" className="h-20 w-auto object-contain mb-6" />
          <div className="text-[11px] tracking-[0.34em] uppercase text-[#14523d] font-semibold">Content Studio</div>
          <h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Sign in</h1>
          <p className="text-[#5a4b4b] mt-2 text-sm">Manage every page and story of legacyindiatravel.com</p>

          {error && (
            <div data-testid="cms-login-error" className="mt-5 bg-[#7c1d1d]/10 border border-[#7c1d1d]/30 text-[#7c1d1d] text-sm px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">Email</span>
              <input data-testid="cms-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="inp" placeholder="you@legacyindiatravel.com" />
            </label>
            <label className="block">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-[#5a4b4b] mb-1.5">Password</span>
              <input data-testid="cms-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="inp" placeholder="••••••••" />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="cms-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#14523d] hover:bg-[#0f3d2c] disabled:opacity-60 text-white py-4 text-xs tracking-[0.28em] uppercase transition-colors rounded-md"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enter Studio
          </button>

          <p className="mt-6 text-xs text-[#8a7c66]">
            Looking for the technical admin dashboard? <a href="/admin/login" className="text-[#7c1d1d] underline">Admin login →</a>
          </p>
        </form>
      </div>
    </div>
  );
}
